import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "../auth/AuthContext";
import {
  listProfiles,
  createProfile as createProfileApi,
  type Profile,
  type CreateProfileInput,
} from "../lib/profiles";

const ACTIVE_KEY = "rt.active_profile";

interface ProfileState {
  profiles: Profile[];
  active: Profile | null;
  loading: boolean;
  refresh: () => Promise<void>;
  setActive: (id: string) => void;
  createProfile: (input: CreateProfileInput) => Promise<Profile>;
}

const ProfileContext = createContext<ProfileState | null>(null);

export function useProfiles(): ProfileState {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error("useProfiles must be used within <ProfileProvider>");
  return ctx;
}

export function ProfileProvider({ children }: { children: ReactNode }) {
  const { account } = useAuth();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [activeId, setActiveId] = useState<string | null>(() => localStorage.getItem(ACTIVE_KEY));
  const [loading, setLoading] = useState(true);

  async function refresh(): Promise<void> {
    if (!account) {
      setProfiles([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await listProfiles();
      setProfiles(res.profiles);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.id]);

  const active = useMemo(() => {
    if (profiles.length === 0) return null;
    return (
      profiles.find((p) => p.id === activeId) ??
      profiles.find((p) => p.is_default) ??
      profiles[0]
    );
  }, [profiles, activeId]);

  function setActive(id: string): void {
    localStorage.setItem(ACTIVE_KEY, id);
    setActiveId(id);
  }

  async function createProfile(input: CreateProfileInput): Promise<Profile> {
    const { profile } = await createProfileApi(input);
    await refresh();
    setActive(profile.id);
    return profile;
  }

  return (
    <ProfileContext.Provider value={{ profiles, active, loading, refresh, setActive, createProfile }}>
      {children}
    </ProfileContext.Provider>
  );
}
